import { useEffect, useRef } from 'react';
import { analyticsAPI } from '../services/api';

// Queue to hold user IDs to be sent
let impressionQueue: Set<string> = new Set();
let flushTimeout: NodeJS.Timeout | null = null;

const FLUSH_INTERVAL = 2000; // Flush every 2 seconds

const flushQueue = async () => {
  if (impressionQueue.size === 0) return;

  const userIds = Array.from(impressionQueue);
  impressionQueue.clear();

  try {
    await analyticsAPI.recordImpressions(userIds);
  } catch (error) {
    console.error('Failed to record impressions:', error);
    // Optionally re-queue failed IDs, but for impressions it might be better to just drop them to avoid loops
  }
};

const addToQueue = (userId: string) => {
  impressionQueue.add(userId);

  if (!flushTimeout) {
    flushTimeout = setTimeout(() => {
      flushQueue();
      flushTimeout = null;
    }, FLUSH_INTERVAL);
  }
};

export const useImpressionTracker = (userId: string) => {
  const elementRef = useRef<HTMLDivElement>(null);
  const hasRecorded = useRef(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && !hasRecorded.current) {
            addToQueue(userId);
            hasRecorded.current = true;
            // Once recorded, we can stop observing this element
            if (elementRef.current) {
              observer.unobserve(elementRef.current);
            }
          }
        });
      },
      {
        threshold: 0.5, // Trigger when 50% of the element is visible
        rootMargin: '0px',
      }
    );

    if (elementRef.current) {
      observer.observe(elementRef.current);
    }

    return () => {
      observer.disconnect();
    };
  }, [userId]);

  return elementRef;
};
